<template>
    <div class="success-page">
        <div class="content">
            <h1>
                <i class="fa-regular fa-circle-check text-white"></i>
            </h1>
            <h3 class="fw-bold" v-if="isCash">Ordine Registrato!</h3>
            <h3 class="fw-bold" v-else>Pagamento Completato!</h3>

            <!-- CASH: remind to confirm and complete payment in person -->
            <template v-if="isCash">
                <p>Grazie! Abbiamo registrato il tuo ordine.</p>
                <p class="mt-2">Ricorda: dovrai confermare e completare il pagamento in sede per attivare il servizio.</p>
                <p>Troverai i dettagli dell'ordine sulla tua e-mail. Se non la trovi, verifica nello spam.</p>
            </template>

            <!-- Online payments (STRIPE/PAYPAL) -->
            <template v-else>
                <p>Grazie per il tuo pagamento. Il tuo servizio è stato registrato con successo.</p>
                <p>Troverai i dettagli dell'ordine sulla tua e-mail. Se non la trovi, verifica nello spam.</p>
            </template>
            <template v-if="isKiosk">
                <div class="kiosk-qr">
                    <img v-if="qrDataUrl" :src="qrDataUrl" alt="QR app Elite Fitness" class="kiosk-qr-img" />
                    <p class="mt-2">Inquadra il QR con il telefono per scaricare l'app e vedere i dettagli dell'ordine.</p>
                </div>
            </template>
            <template v-else-if="isWeb">
                <button @click="openDeepLink" class="btn btn-custom">Scarica o Vai all'App</button>
            </template>
            <template v-else>
                <p class="mt-3">Puoi chiudere questa pagina.</p>
            </template>
        </div>
    </div>
</template>

<script>
import { IS_KIOSK } from '@/config/kiosk';
import QRCode from 'qrcode';

// Universal link: da telefono apre l'app se installata, altrimenti lo store/web.
const KIOSK_APP_URL = 'https://app.elitefitness.it';
const KIOSK_AUTODISMISS_MS = 30000;

export default {
    name: "SuccessPage",
    data() {
        return {
            method: null,
            source: 'web',
            isKiosk: IS_KIOSK,
            qrDataUrl: null,
            dismissTimer: null,
        };
    },
    computed: {
        isCash() {
            return this.method === 'CASH';
        },
        isOnline() {
            return this.method === 'STRIPE' || this.method === 'PAYPAL';
        },
        isWeb() {
            return this.source !== 'app';
        },
    },
    async mounted() {
        if (this.isKiosk) {
            try {
                this.qrDataUrl = await QRCode.toDataURL(KIOSK_APP_URL, { width: 320, margin: 1 });
            } catch (e) {
                this.qrDataUrl = null;
            }
            // Chiusa la vendita, il totem torna anonimo: senza questa pulizia la
            // sessione del socio resterebbe sul dispositivo e il cliente
            // successivo la erediterebbe (stesso effetto di LogoutPage).
            this.clearKioskSession();

            // La pagina di conferma si chiude da sola (nessun timer a schermo).
            this.dismissTimer = setTimeout(() => {
                this.$router.push({ path: '/kiosk' });
            }, KIOSK_AUTODISMISS_MS);
        }
    },
    beforeUnmount() {
        if (this.dismissTimer) clearTimeout(this.dismissTimer);
    },
    created() {
        // Prefer Vue Router query param, fallback to window.location
        let m = null;
        try {
            m = this.$route && this.$route.query ? this.$route.query.method : null;
        } catch (e) {
            m = null;
        }
        let s = null;
        if (!m && typeof window !== 'undefined') {
            const params = new URLSearchParams(window.location.search);
            m = params.get('method');
            s = params.get('source');
        } else if (this.$route && this.$route.query) {
            s = this.$route.query.source;
        }
        this.method = (m || '').toString().toUpperCase();
        this.source = ((s || 'web').toString().toLowerCase() === 'app') ? 'app' : 'web';
    },
    methods: {
        /**
         * Chiusa la vendita il totem torna anonimo: senza questa pulizia la
         * sessione del socio resterebbe sul dispositivo e il cliente successivo
         * la erediterebbe (stessa logica di LogoutPage).
         */
        clearKioskSession() {
            try {
                localStorage.removeItem('userEmail');
                localStorage.removeItem('userToken');
                localStorage.removeItem('tokenExpiration');
            } catch (e) {
                /* storage non disponibile: nulla da ripulire */
            }
        },
        openDeepLink() {
            const deeplink = "elitefitness://elitefitness.it";
            const iosStore = "https://apps.apple.com/it/app/id6670614281";
            const androidStore = "https://play.google.com/store/apps/details?id=it.utopialabs.elitefitness";
            const fallbackWeb = "https://app.elitefitness.it";

            const ua = navigator.userAgent || navigator.vendor || window.opera;
            let storeUrl;

            if (/android/i.test(ua)) {
                storeUrl = androidStore;
            } else if (/iPad|iPhone|iPod/.test(ua) && !window.MSStream) {
                storeUrl = iosStore;
            } else {
                // Desktop o altro → web app
                window.location = fallbackWeb;
                return;
            }

            const now = Date.now();
            window.location = deeplink;

            setTimeout(() => {
                if (Date.now() - now < 1600) {
                    window.location = storeUrl;
                }
            }, 1500);
        }
    }
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@400;600&display=swap');

.success-page {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100vh;
    /* Full page height */
    background-color: #292D7D;
    /* Background blu */
}

.content {
    text-align: center;
    color: white;
    font-family: 'Montserrat', sans-serif;
}

h1 {
    font-size: 3rem;
    font-weight: 600;
}

p {
    font-size: 1.5rem;
    margin-top: 20px;
    margin-bottom: 40px;
}

.success-image {
    width: 150px;
    height: 150px;
    margin-bottom: 30px;
}

/* Stile del pulsante */
.btn-custom {
    font-family: 'Montserrat', sans-serif;
    background-color: #e83539;
    color: white;
    border: none;
    padding: 10px 20px;
    text-transform: uppercase;
    cursor: pointer;
    transition: background-color 0.3s ease;
    border-radius: 0;
    font-size: 1.2rem;
}

.btn-custom:hover {
    background-color: #cc2e31;
    color: white;
}

.kiosk-qr {
    display: flex;
    flex-direction: column;
    align-items: center;
}

.kiosk-qr-img {
    width: 280px;
    height: 280px;
    background: #fff;
    padding: 12px;
    border-radius: 16px;
}

.kiosk-qr p {
    font-size: 1.1rem;
    max-width: 420px;
}
</style>